/**
 * Breadcrumbs — the VISIBLE breadcrumb `<nav>` for detail pages.
 *
 * Rendered from `SeoMeta.breadcrumbs`, which the backend builds from the SAME
 * `(label, url)` trail as the `BreadcrumbList` JSON-LD (`seo::breadcrumb_trail`
 * — one source, two projections). So what a user sees and what a crawler reads
 * cannot disagree, and every detail page gets the same crawlable internal-link
 * mesh it already declares structurally.
 *
 * The last entry is the current page (rendered as text with `aria-current`);
 * earlier entries link to their crumb URL. That URL is an ABSOLUTE backend URL
 * already carrying the locale prefix (byte-identical to the JSON-LD `item`), so
 * it is rendered with a plain `<a href>` rather than the locale-relocalizing
 * `<Link>`: `no-internal-anchor` treats an absolute URL as external, and using
 * the backend URL verbatim keeps the visible href and the structured-data item
 * the same bytes.
 */
import { useTranslations } from '../i18n';
import type { BreadcrumbItem } from '../types/seo';

interface BreadcrumbsProps {
  items?: readonly BreadcrumbItem[];
}

export function Breadcrumbs({ items }: BreadcrumbsProps) {
  const t = useTranslations('common');
  // Fewer than two crumbs is just "the current page" — no trail worth showing.
  if (!items || items.length < 2) return null;

  return (
    <nav aria-label={t('a11y.breadcrumb')} className="mb-4 text-sm text-muted-foreground">
      <ol className="flex flex-wrap items-center">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          // The "/" divider is a CSS pseudo-element (not JSX text) so it stays
          // decorative and out of the accessibility tree / localization.
          const sep =
            index > 0 ? "before:mx-1.5 before:text-muted-foreground/50 before:content-['/']" : '';
          return (
            <li key={item.url} className={`flex items-center ${sep}`}>
              {isLast ? (
                <span aria-current="page" className="font-medium text-foreground">
                  {item.label}
                </span>
              ) : (
                <a
                  href={item.url}
                  className="transition-colors hover:text-foreground hover:underline"
                >
                  {item.label}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
